import { useState } from 'react';
import { VAT_CODES } from '../config/accounting';
import { validateJournalEntry } from '../utils/validation/journalValidation';
import { generateReference } from '../utils/accounting';
import { roundMoney, sumMoney } from '../utils/currency';
import { useJournalActivity } from './useActivityTracking';
import type { JournalEntry, JournalLine } from '../types/accounting';

const createEmptyLine = (description: string = ''): JournalLine => ({
  id: crypto.randomUUID(),
  accountId: '',
  accountCode: '',
  accountName: '',
  debit: 0,
  credit: 0,
  description
});

export function useJournalEntry(initialEntry?: Partial<JournalEntry>) {
  const [entry, setEntry] = useState<Partial<JournalEntry>>({
    date: new Date().toISOString().split('T')[0],
    journalType: initialEntry?.journalType || undefined,
    reference: initialEntry?.reference || '',
    description: initialEntry?.description || '',
    status: initialEntry?.status || 'draft',
    lines: initialEntry?.lines || [createEmptyLine()],
    attachments: initialEntry?.attachments || []
  });

  const { logJournalEntry } = useJournalActivity();

  const calculateTotals = () => {
    const lines = entry.lines || [];
    const totalDebit = sumMoney(lines.map(l => l.debit || 0));
    const totalCredit = sumMoney(lines.map(l => l.credit || 0));
    const totalVat = sumMoney(lines.map(l => l.vatAmount || 0));
    return { totalDebit, totalCredit, totalVat };
  };

  const updateEntry = (updates: Partial<JournalEntry>) => {
    setEntry(prev => {
      const updated = { ...prev, ...updates };
      
      // Générer une nouvelle référence si le type de journal change
      if (updates.journalType && updates.journalType !== prev.journalType) {
        updated.reference = generateReference(updates.journalType, new Date(updated.date || ''));
      }
      
      return updated;
    });
  };

  const addLine = () => {
    setEntry(prev => ({
      ...prev,
      lines: [...(prev.lines || []), createEmptyLine(prev.description)]
    }));
  };

  const removeLine = (index: number) => {
    if ((entry.lines?.length || 0) <= 1) return;
    
    setEntry(prev => ({
      ...prev,
      lines: prev.lines?.filter((_, i) => i !== index)
    }));
  };

  const updateLine = (index: number, updates: Partial<JournalLine>) => {
    setEntry(prev => {
      const newLines = [...(prev.lines || [])];
      newLines[index] = { ...newLines[index], ...updates };
      
      // Recalculer la TVA si nécessaire
      if (updates.vatCode || updates.debit || updates.credit) {
        const line = newLines[index];
        const amount = line.debit || line.credit || 0;
        const vatRate = line.vatCode ? (VAT_CODES as any)[line.vatCode]?.rate || 0 : 0;
        line.vatAmount = roundMoney(amount * (vatRate / 100));
      }
      
      return { ...prev, lines: newLines };
    });
  };

  const addAttachment = (attachment: NonNullable<JournalEntry['attachments']>[number]) => {
    setEntry(prev => {
      const existing = prev.attachments || [];
      // Si un attachement avec le même id existe, le mettre à jour (évite les doublons)
      const existingIndex = existing.findIndex(a => a.id === attachment.id);
      if (existingIndex >= 0) {
        const updated = [...existing];
        updated[existingIndex] = attachment;
        return { ...prev, attachments: updated };
      }
      return { ...prev, attachments: [...existing, attachment] };
    });
  };

  const removeAttachment = (id: string) => {
    setEntry(prev => ({
      ...prev,
      attachments: prev.attachments?.filter(a => a.id !== id) || []
    }));
  };

  const validate = () => {
    const validation = validateJournalEntry(entry);
    return validation;
  };

  const reset = () => {
    setEntry({
      date: new Date().toISOString().split('T')[0],
      journalType: undefined,
      reference: '',
      description: '',
      status: 'draft',
      lines: [createEmptyLine()],
      attachments: []
    });
  };

  const simulateSave = () => {
    // Fonction simulée pour déclencher le logging d'activité
    if (entry.description) {
      const amount = entry.lines?.reduce((sum, line) => sum + (line.debit || line.credit || 0), 0) || 0;
      logJournalEntry('Nouvelle écriture ajoutée', entry.description, amount);
    }
  };

  return {
    entry,
    errors: validate().errors,
    totals: calculateTotals(),
    updateEntry,
    addLine,
    removeLine,
    updateLine,
    addAttachment,
    removeAttachment,
    validate,
    reset,
    simulateSave
  };
}